import {
  Box,
  HStack,
  SimpleGrid,
  Stack,
  useToast,
  VStack,
} from "@chakra-ui/react";
import { useCallback, useContext, useState, useEffect } from "react";
import { KYDDashboardWaitlistBuyOrder } from "@common/interfaces/KYDTicket";
import WaitlistTable from "./WaitlistTable";
import FulfillDialog, {
  FulfillDialogContext,
  withFulfillDialog,
} from "./FulfillDialog";
import { useDialog } from "../../../../Common/Dialog";
import { EventDetailWaitlistSection } from "@common/interfaces/KYDEvent";
import { SectionProps } from "../../sections/sectionsComponents";
import actions from "../../../../actions";
import { Stat } from "../../TicketSales/TicketSalesSection";
import EventDetailTitle from "../../../EventDetailTitle";

const WaitlistSection = ({
  data,
  isLoading = false,
  permissions,
  refetchEvent,
}: SectionProps<EventDetailWaitlistSection["data"]>) => {
  const { open: openFulfillDialog, close: closeFulfillDialog } =
    useContext(FulfillDialogContext);

  const [fulfillingBuyOrders, setFulfillingBuyOrders] = useState<
    KYDDashboardWaitlistBuyOrder[] | null
  >([]);

  const readOnly = !permissions.includes("write");

  const [isFulfilling, setIsFulfilling] = useState(false);

  const toast = useToast();
  const dialog = useDialog();

  const pollForUpdates = useCallback(async () => {
    const sleep = (milliseconds: number) => {
      return new Promise((resolve) => setTimeout(resolve, milliseconds));
    };

    await sleep(5000);
    await refetchEvent({ silent: true });
  }, [data]);

  useEffect(() => {
    console.log("Data changed");
    const startPolling = async () => {
      await pollForUpdates();
    };

    if (data?.waitlist_buy_orders?.some((o) => o.status === "PROCESSING")) {
      setIsFulfilling(true);
      startPolling();
    } else {
      setIsFulfilling(false);
    }
  }, [data]);

  const onClickFulfill = useCallback(
    async (orders: KYDDashboardWaitlistBuyOrder[]) => {
      console.log({ orders });

      setFulfillingBuyOrders(orders);
      openFulfillDialog({
        title: "Fill waitlist requests",
        text: "Here's what will happen:",
      });
    },
    [fulfillingBuyOrders, setFulfillingBuyOrders, openFulfillDialog]
  );

  const onConfirmFill = useCallback(
    async (orders: KYDDashboardWaitlistBuyOrder[]) => {
      try {
        setIsFulfilling(true);
        const response = await actions.ADMIN.fillWaitlistOrders(data.id, {
          waitlist_ids: orders.map((o) => o.id),
        });

        await refetchEvent({ silent: true });
        toast({
          title: "Waitlist requests started",
          description:
            "Requests are now being filled, it might take a few minutes to fill the selected waitlist requests",
          status: "success",
          duration: 5000,
          isClosable: true,
        });
      } catch (e) {
        setIsFulfilling(false);
        dialog({
          title: "Error",
          text: e.message,
        });
      }
      setFulfillingBuyOrders([]);
      closeFulfillDialog();
    },
    [setFulfillingBuyOrders, closeFulfillDialog]
  );

  return (
    <VStack w="full" spacing={8}>
      <FulfillDialog
        orders={fulfillingBuyOrders}
        isLoading={isLoading || isFulfilling}
        onSave={onConfirmFill}
      />
      <Stack spacing={4} w="full" direction={["column", "row"]}>
        <SimpleGrid w="100%" columns={2} gap="6">
          <Stat
            label={"People on waitlist"}
            value={data.realtime_metrics.waitlist_buy_orders_display}
          />
          <Stat
            label={"Tickets on waitlist"}
            value={data.realtime_metrics.waitlist_buy_tickets_display}
          />
        </SimpleGrid>
      </Stack>
      <VStack w="full">
        <HStack w="full">
          <EventDetailTitle>Waitlist</EventDetailTitle>
        </HStack>
        <VStack spacing={4} w="full">
          <Box w="full">
            <WaitlistTable
              timezone={data.timezone}
              isLoading={isFulfilling}
              orders={data.waitlist_buy_orders}
              readOnly={readOnly}
              onClickFulfill={onClickFulfill}
            />
          </Box>
        </VStack>
      </VStack>
    </VStack>
  );
};

export default withFulfillDialog(WaitlistSection);
